// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import { Form } from '../../../generic-components/Form';
import Button from '../../../generic-components/Button';
import LoginCard from '../components/LoginCard';
import LoginLayout from '../components/LoginLayout';

import { useStore } from '../../../store';
import { useLoginMutation } from '../../../hooks/mutations/useLoginMutation';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { handleApplyToLili, extractErrorFromCode } from '../../../helpers';
import { LOCKED_OUT, LOGIN_ERROR_POPUP, USER_BLOCKED_CODE, WELCOME_TO_LILI_SCREEN_DATA } from '../constants';
import { SERVER_ERRORS_DATA } from '../../../constants/serverErrors';
import { LOGIN_NAVIGATION_MAP } from '../components/LoginNavigation/constants';
import { LOGIN_ERRORS, LOGIN_STATUS } from '../constants/messages';
import { IWelcomeFormData } from '../../../types';
import { setToSessionStorage } from '../../../services/storage.service';
import somethingWentWrong from '../../../assets/svg/something-went-wrong.svg';
import Icon from '../../../generic-components/Icon';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { IncorrectStateModal } from '../components/IncorrectStateModal';
import { initBraze } from '../../../services/braze.service';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { GO_TO_PRO_PATH } from '../../dashboard/constants';
import { VIEWS_MAP } from '../../../router/constants';
import { IClientResponse } from '../../profile/components/accountant-account-switcher/types';
import { useAdaService } from '../../../hooks/services/useAdaService';

const Welcome = () => {
  const [formData, setFormData] = useState<IWelcomeFormData>({ email: '', password: '' });
  const { email, password } = formData;
  const fp = useStore((state) => state.getFingerPrint());
  const [isLoadingError, setIsLoadingError] = useState(false);

  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);

  const redirectTo = useStore((state) => state.getRedirection());

  const setRedirection = useStore((state) => state.setRedirection);

  const { LOGIN_CARD, HEADING, I_NEED_HELP_BTN, INPUTS, NEW_TO_LILI, CONTAINER } = WELCOME_TO_LILI_SCREEN_DATA;

  const { HEADER, BUTTON_TEXT, ERROR_TEXT } = LOGIN_ERROR_POPUP;

  const { TEXT, APPLY_NOW_BTN, CONTAINER: NEW_TO_LILI_CONTAINER } = NEW_TO_LILI;

  const setCurrentPage = useStore((state) => state.setCurrentPage);

  const getSummaryData = useStore((state) => state.getSummaryData);

  const resetUserSliceId = useStore((state) => state.resetUserSliceId);

  const searchParams = useStore((state) => state.searchParams);

  const setShowAccountantSwitcherModal = useStore((state) => state.setShowAccountantSwitcherModal);

  const navigate = useNavigate();

  const { startAdaChat } = useAdaService();

  const { refetch: mutateSummary } = useAccountSummaryQuery(async () => {
    const { customer, authorizedUserData } = getSummaryData();
    if (customer) {
      const combinedLists: IClientResponse[] = [
        ...(authorizedUserData?.authorizedUserInvites || []),
        ...(authorizedUserData?.authorizedUserPendingInvites || []),
      ];
      if (redirectTo && redirectTo !== GO_TO_PRO_PATH) {
        setRedirection('');
        navigate(redirectTo);
      } else {
        setRedirection('');
        navigate('/dashboard');
        if (combinedLists.length > 1) {
          setShowAccountantSwitcherModal(true);
        }
      }
      await startAdaChat();
    } else {
      // TODO show error - will take with Robi & Aya
    }
  });

  const handleUserAutoSwitch = () => {
    if (!searchParams) return;
    const qParamsObject = new URLSearchParams(searchParams);
    const crid = qParamsObject.get('crid');
    if (crid) {
      /** Update LocalStorage with new customerId */
      setToSessionStorage({ key: 'customerId', value: crid });
      /** Reset logged-in user id */
      resetUserSliceId(crid);
    }
  };

  const {
    mutate: mutateLogin,
    isSuccess,
    isLoading,
    isError,
    error,
    data: loginResponse,
    reset: resetMutationState,
  } = useLoginMutation(
    {
      loginName: email,
      password,
      clientId: undefined,
      fingerPrint: fp,
    },
    async ({ customerId, status, mfaType }) => {
      if (status === LOGIN_STATUS.SUCCESS) {
        handleUserAutoSwitch();
        await mutateSummary();
        setToSessionStorage({ key: 'customerId', value: JSON.stringify(customerId) });
        await initBraze({ customerId });
      } else if (mfaType || status === LOGIN_STATUS.MFA) {
        setCurrentPage(LOGIN_NAVIGATION_MAP.VERIFICATION_CODE);
        trackMixPanel('Needs MFA code');
      } else if (status === LOGIN_STATUS.PASSWORD_EXPIRED) {
        setCurrentPage(LOGIN_NAVIGATION_MAP.PASSWORD_EXPIRED);
      }
    },
  );

  useEffect(() => {
    if (isError) {
      if (error?.response?.data?.errors?.[0]?.code === USER_BLOCKED_CODE) {
        setCurrentPage(LOGIN_NAVIGATION_MAP.TAKE_5);
      } else if (error?.response?.data?.errors?.[0]?.code === SERVER_ERRORS_DATA.USER_INCORRECT_STATE_CODE) {
        setIsUserMessage(true);
      } else if (
        error?.response?.data?.errors?.[0]?.code === SERVER_ERRORS_DATA.PASSWORD_RECOVERY_IN_PROGRESS_CODE ||
        error?.response?.data?.errors?.[0]?.codeName === SERVER_ERRORS_DATA.PASSWORD_RECOVERY_IN_PROGRESS_CODE_NAME
      ) {
        setIsUserMessage(true);
      } else if (error?.response?.data?.errors?.[0]?.code === SERVER_ERRORS_DATA.INVALID_LOGIN_COUNTRY_CODE) {
        setIsUserMessage(true);
      } else if (error?.response?.data?.errors?.[0]?.data?.url) {
        setIsUserMessage(true);
      } else setIsLoadingError(true);
    }
  }, [isError]);

  useEffect(() => {
    if (!email || !password) return;
    mutateLogin();
  }, [formData]);

  useEffect(() => {
    if (loginResponse !== undefined && loginResponse.data?.status === LOCKED_OUT) {
      setCurrentPage(LOGIN_NAVIGATION_MAP.TAKE_5);
    }
  });

  const handleFormInputsFocus = () => {
    resetMutationState();
    if (isLoadingError) {
      setIsLoadingError(false);
    }
  };

  const handleFormDataChange = () => {
    if (error || isError) {
      resetMutationState();
    }
    if (isLoadingError) {
      setIsLoadingError(false);
    }
  };

  const handleCloseModal = () => {
    setIsUserMessage(false);
  };

  // TODO: ( MULI ) - Add a proper interface for "data"
  const handleFormData = (data: any) => {
    if (!data?.email?.value || !data?.password?.value) return;
    setFormData((prevState) => ({ ...prevState, email: data.email.value, password: data.password.value }));
  };

  const { t } = useTranslation();

  return (
    <>
      {extractErrorFromCode(error) === LOGIN_ERRORS.BAD_REQUEST && isLoadingError && isError && (
        <div className='absolute w-screen h-screen bg-opacity-70 top-0 left-0 bg-bw08 z-50 flex justify-center items-center'>
          <div className='w-[25rem] h-[31.5rem] bg-bw02 opacity-100 rounded-lg flex flex-col items-center'>
            <Icon className='w-[7.625rem] h-[6.75rem] mt-[4.5rem]' src={somethingWentWrong} />
            <span className='leading-[1.375rem] font-metricMedium text-bw08 text-base mt-[2.75rem]'>{t(HEADER)}</span>
            <span className='mt-[0.938rem] text-center font-metric text-bw07 text-base leading-[1.375rem] px-20'>
              {error?.response?.data?.errors?.[0].localizedMessage || t(ERROR_TEXT)}
            </span>
            <Button
              onClick={() => {
                setIsLoadingError(false);
              }}
              className='w-[160px] h-[50px] mt-[6.688rem] bg-action03 font-metricSemibold text-bw00 text-sm rounded-umd'
              id='tryLoginAgain'
              title={t(BUTTON_TEXT)}
            />
          </div>
        </div>
      )}

      <LoginLayout>
        <div className={CONTAINER.CLASS_NAME}>
          <LoginCard className={LOGIN_CARD.CLASS_NAME}>
            <Heading title={t(HEADING.TITLE)} className={HEADING.CLASS_NAME} />
            <Form
              inputsList={INPUTS}
              error={error ? t(extractErrorFromCode(error)) : ''}
              isError={isLoadingError}
              isLoading={isLoading}
              isSuccess={isSuccess}
              onSubmit={handleFormData}
              onInputsFocus={handleFormInputsFocus}
              onFormDataChange={handleFormDataChange}
              submitButtonClassName='w-full bg-action03 hover:bg-action04'
            />
            <Button
              id='help'
              onClick={() => {
                navigate(`/${VIEWS_MAP.RECOVERY}`);
              }}
              title={t(I_NEED_HELP_BTN.TITLE)}
              className={I_NEED_HELP_BTN.CLASS_NAME}
              disable={isLoading}
            />
          </LoginCard>
          <div className={NEW_TO_LILI_CONTAINER.CLASS_NAME}>
            <Heading title={t(TEXT.TITLE)} className={TEXT.CLASS_NAME} />
            <Button
              id='applyToLili'
              title={t(APPLY_NOW_BTN.TITLE)}
              className={APPLY_NOW_BTN.CLASS_NAME}
              onClick={handleApplyToLili}
              disable={isLoading}
            />
          </div>
        </div>
        {isUserMessage && (
          <UserMessage onClick={handleCloseModal} id=''>
            <IncorrectStateModal
              callBack={() => {
                setIsLoadingError(false);
                setIsUserMessage(false);
              }}
              errorCodeName={error?.response?.data?.errors?.[0]?.codeName}
              errorCode={error?.response?.data?.errors?.[0]?.code}
              email={formData.email}
              redirectUrl={error?.response?.data?.errors?.[0]?.data?.url}
            />
          </UserMessage>
        )}
      </LoginLayout>
    </>
  );
};

export default Welcome;
